import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Divider,
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { IconTrash } from '@tabler/icons-react';
import type { DayInterval, FairUsePolicy, FairUseScopeItem } from '../../lib/api/domains/customerCare';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const BUSINESS = [{ from: '08:00', to: '17:00' }];
const MO_FR: DayInterval[][] = [BUSINESS, BUSINESS, BUSINESS, BUSINESS, BUSINESS, [], []];
const ALWAYS: DayInterval[][] = Array.from({ length: 7 }, () => [{ from: '00:00', to: '24:00' }]);

/**
 * Die Felder einer Fair-Use-Vereinbarung — einmal geschrieben, zweimal benutzt:
 * im Admin für den Hausstandard und im Kunden-Flyout für die Vereinbarung eines Kunden.
 *
 * Der Unterschied steckt in `showCustomerAmounts`: Pauschalumsatz und Lizenzkosten gibt
 * es nur je Kunde (später aus dem Control Hub), interne Stundenkosten und Zielmarge sind
 * Hauswerte. Alles andere ist in beiden Fällen dasselbe — und darf deshalb nicht zweimal
 * gepflegt werden, sonst laufen Hausstandard und Kundenvereinbarung auseinander.
 *
 * Bemessung und Stundenzahl bleiben bewusst draußen: Die hängen am Kunden und werden
 * über einen eigenen Endpunkt gespeichert.
 */
export function FairUsePolicyFields({
  value,
  onChange,
  canSeeCalculation,
  showCustomerAmounts,
  included,
  excluded,
  holidayRegions,
  hoursForPreview,
  onUseComputedHours,
}: {
  value: FairUsePolicy;
  onChange: (next: FairUsePolicy) => void;
  /** ADMIN/TEAMLEAD: Beträge sichtbar und änderbar */
  canSeeCalculation: boolean;
  /** true im Kunden-Flyout: Pauschale und Lizenzkosten dieses Kunden */
  showCustomerAmounts: boolean;
  included: FairUseScopeItem[];
  excluded: FairUseScopeItem[];
  /** Auswahl für die Feiertagsregion; leer = der gespeicherte Wert bleibt stehen. */
  holidayRegions: { value: string; label: string }[];
  /** Für die Vorschau der Staffel („→ 18,75 h/Monat") */
  hoursForPreview?: number | null;
  /** Nur im Flyout: berechnete Stundenzahl als Kontingent übernehmen */
  onUseComputedHours?: (hours: number) => void;
}) {
  const set = (patch: Partial<FairUsePolicy>) => onChange({ ...value, ...patch });
  const calc = value.calculation;
  const tol = value.tolerance;

  const setCalc = (patch: Partial<FairUsePolicy['calculation']>) =>
    onChange({
      ...value,
      // Wer die Zahlen anfasst, macht sie zur Handeingabe: Ein späterer Abgleich aus
      // dem Control Hub soll eine bewusst gesetzte Kondition nicht überschreiben.
      calculation: { ...value.calculation, ...patch, source: 'MANUAL', syncedAt: null },
    });
  const setTol = (patch: Partial<FairUsePolicy['tolerance']>) =>
    onChange({ ...value, tolerance: { ...value.tolerance, ...patch } });
  const setAdj = (patch: Partial<FairUsePolicy['adjustment']>) =>
    onChange({ ...value, adjustment: { ...value.adjustment, ...patch } });
  const setStep = (index: number, patch: Partial<FairUsePolicy['adjustment']['steps'][number]>) =>
    onChange({
      ...value,
      adjustment: {
        ...value.adjustment,
        steps: value.adjustment.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)),
      },
    });
  const addStep = () => {
    const last = value.adjustment.steps[value.adjustment.steps.length - 1];
    onChange({
      ...value,
      adjustment: {
        ...value.adjustment,
        steps: [
          ...value.adjustment.steps,
          {
            fromPercent: (last?.fromPercent ?? value.adjustment.triggerPercent) + 25,
            raisePercent: (last?.raisePercent ?? 0) + 25,
          },
        ],
      },
    });
  };
  const removeStep = (index: number) =>
    onChange({
      ...value,
      adjustment: {
        ...value.adjustment,
        steps: value.adjustment.steps.filter((_, i) => i !== index),
      },
    });

  const setWindow = (patch: Partial<FairUsePolicy['serviceWindow']>) =>
    onChange({ ...value, serviceWindow: { ...value.serviceWindow, ...patch } });
  const setDay = (index: number, intervals: DayInterval[]) =>
    setWindow({ days: value.serviceWindow.days.map((d, i) => (i === index ? intervals : d)) });
  const setInterval = (index: number, slot: number, patch: Partial<DayInterval>) =>
    setDay(
      index,
      (value.serviceWindow.days[index] ?? []).map((iv, i) => (i === slot ? { ...iv, ...patch } : iv))
    );
  const setWeek = (days: DayInterval[][]) => setWindow({ days });

  const toggleKey = (field: 'includedKeys' | 'excludedKeys', key: string, on: boolean) =>
    set({
      [field]: on ? [...new Set([...value[field], key])] : value[field].filter((k) => k !== key),
    } as Partial<FairUsePolicy>);

  /**
   * Dieselbe Rechnung wie `contingentFromCalculation` im Backend — hier nur, damit das
   * Ergebnis beim Tippen mitläuft. Maßgeblich ist, was gespeichert wird.
   */
  const computed = (() => {
    const { flatRateMonthly, licenseCostMonthly, internalCostPerHour, targetMarginPercent } = calc;
    if (flatRateMonthly == null || internalCostPerHour == null || internalCostPerHour <= 0) return null;
    const serviceRevenue = Math.round((flatRateMonthly - (licenseCostMonthly ?? 0)) * 100) / 100;
    if (serviceRevenue <= 0) return null;
    const breakEvenHours = Math.round((serviceRevenue / internalCostPerHour) * 100) / 100;
    const plannedHours = Math.round(breakEvenHours * (1 - targetMarginPercent / 100) * 100) / 100;
    return {
      serviceRevenue,
      breakEvenHours,
      plannedHours,
      licenseSharePercent:
        flatRateMonthly > 0 ? Math.round(((licenseCostMonthly ?? 0) / flatRateMonthly) * 100) : 0,
    };
  })();

  /**
   * Der Zusammenhang, der die Toleranz begründet, und zwar auch ohne Pauschale: Bei
   * 33 % Zielmarge ist die Marge bei 149 % des Kontingents aufgebraucht.
   */
  const marginGonePercent =
    calc.targetMarginPercent < 100
      ? Math.round(100 / (1 - calc.targetMarginPercent / 100))
      : null;

  const scopeList = (field: 'includedKeys' | 'excludedKeys', items: FairUseScopeItem[]) => (
    <Stack gap={6}>
      {items.map((item) => (
        <Checkbox
          key={item.key}
          size="xs"
          checked={value[field].includes(item.key)}
          onChange={(e) => toggleKey(field, item.key, e.currentTarget.checked)}
          label={
            <div>
              <Text size="sm">{item.label}</Text>
              <Text size="xs" c="dimmed">
                {item.detail}
              </Text>
            </div>
          }
        />
      ))}
    </Stack>
  );

  return (
    <Stack gap="md">
      {canSeeCalculation && (
        <div>
          <Text size="xs" tt="uppercase" fw={600} c="dimmed" mb={2}>
            {showCustomerAmounts ? 'Woraus die Stundenzahl kommt' : 'Kalkulationsgrundlage'}
          </Text>
          <Text size="xs" c="dimmed" mb={6}>
            {showCustomerAmounts
              ? '„Pauschale ÷ Stundensatz" führt in die Irre — in der Flat stecken Lizenzen und Tools, die keine Arbeitszeit sind. Gerechnet wird der Serviceanteil gegen die internen Vollkosten je Stunde.'
              : 'Vorbelegung für neue Vereinbarungen. Die Beträge je Kunde (Pauschale, Lizenzen) stehen in der Kundenakte.'}
          </Text>
          <Group gap="sm" wrap="wrap" align="flex-start">
            {showCustomerAmounts && (
              <>
                <NumberInput
                  size="xs"
                  label="Pauschale"
                  w={150}
                  min={0}
                  decimalScale={2}
                  suffix=" €/Monat"
                  value={calc.flatRateMonthly ?? ''}
                  onChange={(v) => setCalc({ flatRateMonthly: typeof v === 'number' ? v : null })}
                />
                <NumberInput
                  size="xs"
                  label="davon Lizenzen und Tools"
                  w={190}
                  min={0}
                  decimalScale={2}
                  suffix=" €/Monat"
                  value={calc.licenseCostMonthly ?? ''}
                  onChange={(v) => setCalc({ licenseCostMonthly: typeof v === 'number' ? v : null })}
                />
              </>
            )}
            <NumberInput
              size="xs"
              label="interne Kosten je Stunde"
              w={180}
              min={0}
              decimalScale={2}
              suffix=" €"
              value={calc.internalCostPerHour ?? ''}
              onChange={(v) => setCalc({ internalCostPerHour: typeof v === 'number' ? v : null })}
              description="Personal inkl. Overhead, nicht der Verkaufspreis"
            />
            <NumberInput
              size="xs"
              label="Zielmarge"
              w={130}
              min={0}
              max={90}
              suffix=" %"
              value={calc.targetMarginPercent}
              onChange={(v) => setCalc({ targetMarginPercent: typeof v === 'number' ? v : 0 })}
            />
          </Group>

          {calc.source === 'CONTROL_HUB' && calc.syncedAt && (
            <Text size="xs" c="dimmed" mt={4}>
              Beträge aus dem Control Hub, Stand {new Date(calc.syncedAt).toLocaleDateString('de-DE')}.
              Eine Änderung hier macht sie zur Handeingabe — der nächste Abgleich lässt sie dann stehen.
            </Text>
          )}

          {computed ? (
            <Alert color="teal" variant="light" p="xs" mt="xs">
              <Text size="xs">
                Serviceanteil {computed.serviceRevenue.toFixed(2)} € (
                {100 - computed.licenseSharePercent} % der Pauschale) · Deckungspunkt bei{' '}
                {computed.breakEvenHours} h · <b>Vorschlag: {computed.plannedHours} h je Monat</b>.
                {marginGonePercent != null && (
                  <>
                    {' '}
                    Ab {marginGonePercent} % des Kontingents trägt sich der Monat nicht mehr — dort
                    sollte die Einzelgrenze ungefähr liegen.
                  </>
                )}
              </Text>
              <Group gap="xs" mt={6}>
                {onUseComputedHours && (
                  <Button
                    size="compact-xs"
                    variant="light"
                    onClick={() => onUseComputedHours(computed.plannedHours)}
                  >
                    Als Kontingent übernehmen
                  </Button>
                )}
                {marginGonePercent != null && (
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    onClick={() => setTol({ singlePercent: Math.max(100, marginGonePercent) })}
                  >
                    Einzelgrenze auf {Math.max(100, marginGonePercent)} % setzen
                  </Button>
                )}
              </Group>
            </Alert>
          ) : (
            marginGonePercent != null && (
              <Text size="xs" c="dimmed" mt={6}>
                Bei {calc.targetMarginPercent} % Zielmarge ist die Marge bei{' '}
                <b>{marginGonePercent} %</b> des Kontingents aufgebraucht — dort sollte die
                Einzelgrenze liegen.
                {showCustomerAmounts && ' Pauschale und interne Kosten eintragen für den Stundenvorschlag.'}
              </Text>
            )
          )}
        </div>
      )}

      <Divider />

      <div>
        <Text size="xs" tt="uppercase" fw={600} c="dimmed" mb={2}>
          Toleranz
        </Text>
        <Text size="xs" c="dimmed" mb={6}>
          Je länger der Zeitraum, desto enger die Grenze: Ein einzelner Monat darf ausschlagen,
          eine Serie weniger, im Jahresmittel bleibt kaum Spielraum.
        </Text>
        <Group gap="sm" wrap="wrap" align="flex-start">
          <NumberInput
            size="xs"
            label="Einzelner Zeitraum"
            w={150}
            min={100}
            max={400}
            suffix=" %"
            value={tol.singlePercent}
            onChange={(v) => setTol({ singlePercent: typeof v === 'number' ? v : 100 })}
          />
          <NumberInput
            size="xs"
            label="Mehrere in Folge"
            w={150}
            min={100}
            max={400}
            suffix=" %"
            value={tol.consecutivePercent}
            onChange={(v) => setTol({ consecutivePercent: typeof v === 'number' ? v : 100 })}
          />
          <NumberInput
            size="xs"
            label="ab … Zeiträumen"
            w={130}
            min={2}
            max={12}
            value={tol.consecutiveCount}
            onChange={(v) => setTol({ consecutiveCount: typeof v === 'number' ? v : 2 })}
          />
          <NumberInput
            size="xs"
            label="Jahresdurchschnitt"
            w={150}
            min={100}
            max={400}
            suffix=" %"
            value={tol.rollingYearPercent}
            onChange={(v) => setTol({ rollingYearPercent: typeof v === 'number' ? v : 100 })}
          />
        </Group>
        {(tol.consecutivePercent > tol.singlePercent ||
          tol.rollingYearPercent > tol.consecutivePercent) && (
          <Text size="xs" c="orange" mt={4}>
            Die Leiter läuft verkehrt herum — beim Speichern werden die engeren Grenzen auf die
            weitere gedeckelt.
          </Text>
        )}
      </div>

      <Divider />

      <div>
        <Text size="xs" tt="uppercase" fw={600} c="dimmed" mb={2}>
          Anpassung zum Folgejahr
        </Text>
        <Text size="xs" c="dimmed" mb={6}>
          Liegt der Jahresdurchschnitt über der Auslöseschwelle, steigt das Kontingent nach
          dieser Staffel. Bewusst nicht die volle Lücke: Der Rest ist unsere Aufgabe — Ursachen
          beseitigen, automatisieren, vorsorgen.
        </Text>
        <NumberInput
          size="xs"
          label="Auslöseschwelle"
          w={170}
          min={100}
          max={500}
          suffix=" %"
          value={value.adjustment.triggerPercent}
          onChange={(v) => setAdj({ triggerPercent: typeof v === 'number' ? v : 100 })}
          description="Jahresdurchschnitt"
        />
        <Stack gap={6} mt="xs">
          {value.adjustment.steps.map((step, index) => (
            <Group key={index} gap="xs" align="flex-end" wrap="nowrap">
              <NumberInput
                size="xs"
                label={index === 0 ? 'ab Jahresmittel' : undefined}
                w={140}
                min={100}
                max={1000}
                suffix=" %"
                value={step.fromPercent}
                onChange={(v) => setStep(index, { fromPercent: typeof v === 'number' ? v : 100 })}
              />
              <NumberInput
                size="xs"
                label={index === 0 ? 'Kontingent steigt um' : undefined}
                w={160}
                min={0}
                max={500}
                suffix=" %"
                value={step.raisePercent}
                onChange={(v) => setStep(index, { raisePercent: typeof v === 'number' ? v : 0 })}
              />
              {hoursForPreview != null && (
                <Text size="xs" c="dimmed" pb={6}>
                  → {Math.round(hoursForPreview * (1 + step.raisePercent / 100) * 100) / 100} h/Monat
                </Text>
              )}
              <Button
                size="compact-xs"
                variant="subtle"
                color="red"
                mb={2}
                onClick={() => removeStep(index)}
              >
                Entfernen
              </Button>
            </Group>
          ))}
          <Group>
            <Button size="compact-xs" variant="light" onClick={addStep}>
              Stufe ergänzen
            </Button>
          </Group>
        </Stack>
      </div>

      <Divider />

      <Group gap="sm" align="flex-start" wrap="wrap">
        <div>
          <Switch
            size="xs"
            mt={26}
            label="Rest wandert in den Folgemonat"
            checked={value.carryOver}
            onChange={(e) => set({ carryOver: e.currentTarget.checked })}
          />
        </div>
        {value.carryOver && (
          <NumberInput
            size="xs"
            label="höchstens"
            w={140}
            min={0}
            max={200}
            suffix=" %"
            value={value.carryOverMaxPercent}
            onChange={(v) => set({ carryOverMaxPercent: typeof v === 'number' ? v : 100 })}
            description="eines Kontingents"
          />
        )}
      </Group>

      <div>
        <Text size="xs" tt="uppercase" fw={600} c="dimmed" mb={2}>
          Servicezeit
        </Text>
        <Text size="xs" c="dimmed" mb={6}>
          Daran rechnet die SLA-Uhr. Außerhalb steht sie: Ein Ticket von Freitag 17:30
          läuft erst am Montag um 08:00 weiter — nicht über das Wochenende.
        </Text>

        <Stack gap={4}>
          {WEEKDAYS.map((label, index) => {
            const intervals = value.serviceWindow.days[index] ?? [];
            const open = intervals.length > 0;
            return (
              <Group key={label} gap="xs" wrap="nowrap" align="center">
                <Checkbox
                  size="xs"
                  w={64}
                  label={label}
                  checked={open}
                  onChange={(e) =>
                    setDay(index, e.currentTarget.checked ? [{ from: '08:00', to: '17:00' }] : [])
                  }
                />
                {open ? (
                  <>
                    {intervals.map((interval, slot) => (
                      <Group key={slot} gap={4} wrap="nowrap">
                        <TimeInput
                          size="xs"
                          w={95}
                          value={interval.from}
                          onChange={(e) => setInterval(index, slot, { from: e.currentTarget.value })}
                        />
                        <Text size="xs" c="dimmed">
                          –
                        </Text>
                        <TimeInput
                          size="xs"
                          w={95}
                          value={interval.to}
                          onChange={(e) => setInterval(index, slot, { to: e.currentTarget.value })}
                        />
                        {intervals.length > 1 && (
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="red"
                            onClick={() =>
                              setDay(index, intervals.filter((_, i) => i !== slot))
                            }
                          >
                            <IconTrash size={12} />
                          </ActionIcon>
                        )}
                      </Group>
                    ))}
                    {intervals.length < 3 && (
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        onClick={() =>
                          setDay(index, [...intervals, { from: '13:00', to: '16:00' }])
                        }
                      >
                        + Fenster
                      </Button>
                    )}
                  </>
                ) : (
                  <Text size="xs" c="dimmed">
                    geschlossen
                  </Text>
                )}
              </Group>
            );
          })}
        </Stack>

        <Group gap="sm" mt="xs" wrap="wrap" align="flex-end">
          <Select
            size="xs"
            w={230}
            label="Gesetzliche Feiertage"
            description="an diesen Tagen steht die Uhr"
            value={value.serviceWindow.holidayRegion}
            allowDeselect={false}
            data={holidayRegions.length ? holidayRegions : [{ value: value.serviceWindow.holidayRegion, label: value.serviceWindow.holidayRegion }]}
            onChange={(v) =>
              v && set({ serviceWindow: { ...value.serviceWindow, holidayRegion: v } })
            }
          />
          <Button size="compact-xs" variant="subtle" onClick={() => setWeek(MO_FR)}>
            Mo–Fr 08–17
          </Button>
          <Button size="compact-xs" variant="subtle" onClick={() => setWeek(ALWAYS)}>
            Rund um die Uhr
          </Button>
        </Group>
      </div>

      <Divider />

      <div>
        <Text size="xs" tt="uppercase" fw={600} c="dimmed" mb={6}>
          In der Pauschale enthalten
        </Text>
        {scopeList('includedKeys', included)}
      </div>

      <div>
        <Text size="xs" tt="uppercase" fw={600} c="dimmed" mb={6}>
          Wird gesondert beauftragt und berechnet
        </Text>
        {scopeList('excludedKeys', excluded)}
      </div>

      <Textarea
        label="Wenn der Rahmen dauerhaft nicht passt"
        autosize
        minRows={2}
        value={value.overrunHandling}
        onChange={(e) => set({ overrunHandling: e.currentTarget.value })}
      />

      <Textarea
        label="Sonderregelung"
        placeholder={'Alles, was der Katalog nicht abbildet — z. B. „Standort Hamburg zählt nicht mit".'}
        autosize
        minRows={2}
        value={value.note ?? ''}
        onChange={(e) => set({ note: e.currentTarget.value || null })}
      />
    </Stack>
  );
}
